import { gql } from '@apollo/client';
import type { CustomEvalTestDefinition } from './settings';

export const SEQUENCES_QUERY = gql`
  query Sequences {
    sequences {
      id
      name
      description
      status
      executionIntervalMinutes
      maxRetries
      timeoutSeconds
      globalVariables
      analysisVariables
      ignoredHarmCategories
      ignoredGraniteCategories
      llamaGuardEnabled
      graniteGuardianEnabled
      semanticAnalysisEnabled
      ignoredSemanticCategories
      customTests
      scheduledEnabled
      scheduledDays
      scheduledStartTime
      scheduledFrequencyMinutes
      scheduledMaxTimeoutMinutes
      nextScheduledRun
      isRunning
      createdAt
      lastExecuted
      steps {
        id
        stepNumber
        name
        stepType
        url
        httpMethod
        headers
        requestBody
        requestFormat
        responseVariableMappings
        analyzeForBias
        analysisText
        biasAnalysisPrompt
        waitSeconds
        retryOnFailure
        skipConditionVariable
        skipConditionOperator
        skipConditionValue
        actionBlocks
        variableDefinitions
        ragAnalysisEnabled
        ragQueryVariable
        ragContextVariable
        ragResponseVariable
        fileAttachments
      }
      files {
        id
        originalFilename
        fileType
        mimeType
        fileSize
        variableName
        description
        hasTextContent
        fileUrl
        createdAt
      }
    }
  }
`;

export const SEQUENCE_EXECUTIONS_QUERY = gql`
  query SequenceExecutions($sequenceId: ID, $limit: Int = 20) {
    sequenceExecutions(sequenceId: $sequenceId, limit: $limit) {
      id
      status
      startedAt
      completedAt
      totalSteps
      completedSteps
      failedSteps
      overallBiasScore
      violationsDetected
      executionVariables
      biasAnalysisResults
      errorMessage
      customTestsPerformed
      customTestsAllPassed
      customTestsConfig
      customTestsResults
      stepExecutions {
        id
        status
        startedAt
        completedAt
        resolvedUrl
        responseStatusCode
        responseTimeMs
        biasAnalysisPerformed
        biasScore
        biasCategories
        biasAnalysisDetails
        confidenceScore
        extractedVariables
        errorMessage
        retryCount
      }
    }
  }
`;

// Removed SEQUENCE_TEMPLATES_QUERY - not implemented in backend

export const CREATE_SEQUENCE_MUTATION = gql`
  mutation CreateSequence($input: CreateSequenceInput!) {
    createSequence(input: $input) {
      id
      name
      description
      status
      executionIntervalMinutes
      globalVariables
      analysisVariables
      ignoredHarmCategories
      ignoredGraniteCategories
      llamaGuardEnabled
      graniteGuardianEnabled
      semanticAnalysisEnabled
      ignoredSemanticCategories
      customTests
    }
  }
`;

export const CREATE_SEQUENCE_FROM_TEMPLATE_MUTATION = gql`
  mutation CreateSequenceFromTemplate($templateId: ID!, $name: String!, $variables: String!) {
    createSequenceFromTemplate(templateId: $templateId, name: $name, variables: $variables) {
      id
      name
      description
      status
    }
  }
`;

export const UPDATE_SEQUENCE_MUTATION = gql`
  mutation UpdateSequence($id: ID!, $input: CreateSequenceInput!) {
    updateSequence(id: $id, input: $input) {
      id
      name
      description
      status
      executionIntervalMinutes
      maxRetries
      timeoutSeconds
      globalVariables
      analysisVariables
      ignoredHarmCategories
      ignoredGraniteCategories
      llamaGuardEnabled
      graniteGuardianEnabled
      semanticAnalysisEnabled
      ignoredSemanticCategories
      customTests
      scheduledEnabled
      scheduledDays
      scheduledStartTime
      scheduledFrequencyMinutes
      scheduledMaxTimeoutMinutes
      steps {
        id
        stepNumber
        name
        stepType
        url
        httpMethod
        analyzeForBias
        analysisText
      }
    }
  }
`;

export const DELETE_SEQUENCE_MUTATION = gql`
  mutation DeleteSequence($id: ID!) {
    deleteSequence(id: $id)
  }
`;

export const EXECUTE_SEQUENCE_MUTATION = gql`
  mutation ExecuteSequence($sequenceId: ID!) {
    executeSequence(sequenceId: $sequenceId) {
      id
      status
      message
      executionId
      startedAt
      totalSteps
      completedSteps
    }
  }
`;

export const GENERATE_CUSTOM_TESTS_FROM_PROMPT_MUTATION = gql`
  mutation GenerateCustomTestsFromPrompt($input: GenerateCustomTestsFromPromptInput!) {
    generateCustomTestsFromPrompt(input: $input) {
      tests
    }
  }
`;

// TypeScript interfaces
export interface FileAttachment {
  fileId: string;
  fieldName: string;  // Form field name for the file
  variableName?: string;  // Variable to store file content
}

export interface SequenceFile {
  id: string;
  originalFilename: string;
  fileType: 'context' | 'attachment' | 'both';
  mimeType: string;
  fileSize: number;
  variableName: string;
  description: string;
  hasTextContent: boolean;
  fileUrl: string;
  createdAt: string;
}

export interface SequenceStep {
  id: string;
  stepNumber: number;
  name: string;
  stepType: string;
  url: string;
  httpMethod: string;
  headers: any;
  requestBody: string;
  requestFormat: string;
  responseVariableMappings: any;
  analyzeForBias: boolean;
  analysisText: string;
  biasAnalysisPrompt: string;
  waitSeconds: number;
  retryOnFailure: boolean;
  skipConditionVariable: string;
  skipConditionOperator: string;
  skipConditionValue: string;
  actionBlocks: any[];
  variableDefinitions: any[];
  ragAnalysisEnabled: boolean;
  ragQueryVariable: string;
  ragContextVariable: string;
  ragResponseVariable: string;
  fileAttachments: FileAttachment[];
}

export interface Sequence {
  id: string;
  name: string;
  description: string;
  status: string;
  executionIntervalMinutes: number;
  maxRetries: number;
  timeoutSeconds: number;
  globalVariables: string;
  analysisVariables: string[];
  ignoredHarmCategories: string[];
  ignoredGraniteCategories: string[];
  llamaGuardEnabled: boolean;
  graniteGuardianEnabled: boolean;
  semanticAnalysisEnabled: boolean;
  ignoredSemanticCategories: string[];
  customTests?: CustomEvalTestDefinition[];
  scheduledEnabled: boolean;
  scheduledDays: number[];
  scheduledStartTime?: string;
  scheduledFrequencyMinutes: number;
  scheduledMaxTimeoutMinutes: number;
  nextScheduledRun?: string;
  isRunning: boolean;
  createdAt: string;
  lastExecuted?: string;
  steps: SequenceStep[];
  files: SequenceFile[];
}

export interface SequenceStepExecution {
  id: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  resolvedUrl: string;
  responseStatusCode?: number;
  responseTimeMs?: number;
  biasAnalysisPerformed: boolean;
  biasScore?: number;
  biasCategories: string[];
  biasAnalysisDetails: string;
  confidenceScore?: number;
  extractedVariables: string;
  errorMessage: string;
  retryCount: number;
}

export interface SequenceExecution {
  id: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  totalSteps: number;
  completedSteps: number;
  failedSteps: number;
  overallBiasScore?: number;
  violationsDetected: number;
  executionVariables: string;
  biasAnalysisResults: string;
  errorMessage: string;
  customTestsPerformed?: boolean;
  customTestsAllPassed?: boolean | null;
  customTestsConfig?: unknown;
  customTestsResults?: unknown;
  stepExecutions: SequenceStepExecution[];
}

export interface SequenceTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  templateConfig: string;
  requiredVariables: string[];
  usageCount: number;
}
